import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { PresignedUrlService } from '../../../../../shared/providers/presigned-url/presigned-url.service';
import { RequestsProvider } from '../../../../../shared/providers/requests/requests';
import { DocumentsProvider } from '../../../../../shared/providers/document/documents.provider';
import { NetworkProvider } from '../../../../../shared/providers/network/network';
import { EquipementEnergy, MesureEvenement, MesureEvenementPhoto } from '../../../../../shared/models/v2/releve.model';
import { UploadPresignedUrlDto } from '../../../../../api/models/upload-presigned-url-dto';
import { LoggerWrapper } from '../../../../../shared/providers/logger/logger-wrapper.service';
import { StorageProvider } from '../../../../../shared/providers/storage/storage';
import { ReleveDto } from '../../../../../api/models/releve-dto';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoadingController } from '@ionic/angular';
import { SelectorPhoto } from '../../../../../shared/providers/photos.service';
import { UuidService } from '../../../../../shared/providers/uuid/uuid.service';
import dayjs from 'dayjs';

export type RequestPostS3 = {
  presignedUrlRequest: UploadPresignedUrlDto;
  data: string;
};

export type RequestDeleteS3 = {
  id_document: string;
};

@Injectable({
  providedIn: 'root',
})
export class CrReleveCompteurPhotoService {
  private readonly logger = inject(LoggerWrapper);
  private readonly destroyRef = inject(DestroyRef);
  private readonly uuid = inject(UuidService);

  private readonly presignedUrlService: PresignedUrlService = inject(PresignedUrlService);
  private readonly requestsProvider: RequestsProvider = inject(RequestsProvider);
  private readonly documentsProvider: DocumentsProvider = inject(DocumentsProvider);
  private readonly storageProvider: StorageProvider = inject(StorageProvider);
  private readonly networkProvider: NetworkProvider = inject(NetworkProvider);
  private readonly loadingCtrl: LoadingController = inject(LoadingController);

  private readonly isOnline = signal<boolean>(false);

  constructor() {
    this.networkProvider.networkStatusSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.isOnline.set(this.networkProvider.isConnected());
    });
  }

  async syncPhotosWithS3(previous: MesureEvenementPhoto[], next: MesureEvenementPhoto[], storageId: string) {
    await Promise.all(previous.map((photo) => this.deletePhotoFromS3(photo.id_document, storageId)));

    await Promise.all(next.map((photo) => this.postPhotoToS3(photo, storageId)));
  }

  async handlePhotosChanged(
    updatedPhotos: SelectorPhoto[],
    mesureEvenement: MesureEvenement,
    equipementEnergy: EquipementEnergy,
    codeRegistre?: string | number
  ): Promise<MesureEvenementPhoto[]> {
    const previousPhotos = mesureEvenement.photos ?? [];
    const nextPhoto = updatedPhotos.at(-1) ?? null;

    let nextMesurePhotos: MesureEvenementPhoto[] = [];
    if (nextPhoto) {
      const today = dayjs().format('YYYY-MM-DD');
      const dkCodeInstallation = equipementEnergy.dk_code_installation_principale;
      const numeroCompteur = equipementEnergy.equipment.code_equipement;

      let photoName = `Photo-${today}-${dkCodeInstallation}-${numeroCompteur}`;
      if (codeRegistre !== undefined) {
        photoName += `-${codeRegistre}`;
      }

      const nextMesurePhoto: MesureEvenementPhoto = {
        id_document: this.uuid.v4(),
        format: nextPhoto.format,
        data: nextPhoto.data,
        size: nextPhoto.size,
        name: photoName,
      };
      nextMesurePhotos = [nextMesurePhoto];
    }

    await this.syncPhotosWithS3(previousPhotos, nextMesurePhotos, mesureEvenement.idEquipement);

    return nextMesurePhotos;
  }

  private async postPhotoToS3(photo: MesureEvenementPhoto, storageId: string) {
    const presignedRequest: UploadPresignedUrlDto = {
      id_document: photo.id_document,
      nom: photo.name,
      mime_type: photo.format,
      taille: photo.size,
      type: 'photo',
    };

    const requestS3: RequestPostS3 = {
      presignedUrlRequest: presignedRequest,
      data: photo.data,
    };
    const loading = await this.loadingCtrl.create({
      message: 'Ajout de la photo en cours',
    });
    await loading.present();
    try {
      if (this.isOnline()) {
        await this.postPhotoToS3Online(requestS3, storageId);
      } else {
        await this.addPostPhotoRequestToStorage(requestS3, storageId);
      }
    } finally {
      await loading.dismiss();
    }
  }

  private async postPhotoToS3Online(request: RequestPostS3, storageId: string) {
    try {
      const presignedUrlResponse = await this.presignedUrlService.getPresignedUrlForUpload(request.presignedUrlRequest);
      let blob = await fetch(request.data).then((res) => res.blob());
      let fileToUpload = new File([blob], request.presignedUrlRequest.nom);
      await this.requestsProvider.put(presignedUrlResponse.url, fileToUpload, {
        'Content-Type': request.presignedUrlRequest.mime_type,
      });
      await this.removePostPhotoRequestFromStorage(storageId, request.presignedUrlRequest.id_document);
    } catch (error) {
      this.logger.error(error, 'Upload S3 failed');
      await this.addPostPhotoRequestToStorage(request, storageId);
    }
  }

  private async deletePhotoFromS3(id_document: string, storageId: string) {
    const request: RequestDeleteS3 = {
      id_document: id_document,
    };

    if (this.isOnline()) {
      await this.deletePhotoFromS3Online(request, storageId);
    } else {
      await this.addDeleteRequestToStorage(request, storageId);
    }
  }

  private async deletePhotoFromS3Online(request: RequestDeleteS3, storageId: string) {
    try {
      await this.documentsProvider.deleteDocument(request.id_document);
      await this.removeDeleteRequestToStorage(storageId, request.id_document);
    } catch (error) {
      this.logger.error(error, 'Delete S3 failed');
      await this.addDeleteRequestToStorage(request, storageId);
    }
  }

  private async addPostPhotoRequestToStorage(request: RequestPostS3, storageId: string) {
    let data: RequestPostS3[] = (await this.storageProvider.get(this.getKeyPostPhotoRequest(storageId), false)) || [];

    const exists = data.some(
      (item) => item.presignedUrlRequest?.id_document === request.presignedUrlRequest?.id_document
    );

    if (!exists) {
      data.push(request);
      await this.storageProvider.set(this.getKeyPostPhotoRequest(storageId), data, false);
    }
  }

  private async removePostPhotoRequestFromStorage(storageId: string, id_document: string) {
    let data: RequestPostS3[] = (await this.storageProvider.get(this.getKeyPostPhotoRequest(storageId), false)) || [];
    data = data.filter((request: RequestPostS3) => request.presignedUrlRequest.id_document !== id_document);
    if (data.length > 0) {
      await this.storageProvider.set(this.getKeyPostPhotoRequest(storageId), data, false);
    } else {
      await this.storageProvider.remove(this.getKeyPostPhotoRequest(storageId));
    }
  }

  private async addDeleteRequestToStorage(request: RequestDeleteS3, storageId: string) {
    let data: RequestDeleteS3[] =
      (await this.storageProvider.get(this.getKeyDeletePhotoRequest(storageId), false)) || [];

    const exists = data.some((item: RequestDeleteS3) => item.id_document === request.id_document);

    if (!exists) {
      data.push(request);
      await this.storageProvider.set(this.getKeyDeletePhotoRequest(storageId), data, false);
    }
  }

  private async removeDeleteRequestToStorage(storageId: string, id_document: string) {
    let data: RequestDeleteS3[] =
      (await this.storageProvider.get(this.getKeyDeletePhotoRequest(storageId), false)) || [];
    data = data.filter((request: RequestDeleteS3) => request.id_document !== id_document);
    if (data.length > 0) {
      await this.storageProvider.set(this.getKeyDeletePhotoRequest(storageId), data, false);
    } else {
      await this.storageProvider.remove(this.getKeyDeletePhotoRequest(storageId));
    }
  }

  private getKeyPostPhotoRequest(id_storage: string) {
    return 'post_releve_photo_' + id_storage;
  }

  private getKeyDeletePhotoRequest(id_storage: string) {
    return 'delete_releve_photo_' + id_storage;
  }

  async retryStoredRequests(releve: ReleveDto) {
    const storageIds = releve?.mesureEvenements.map((mesure) => mesure.idEquipement) ?? [];
    await this.retryPostPhotoToS3(storageIds);
    await this.retryDeletePhotoFromS3(storageIds);
  }

  async retryPostPhotoToS3(storageIds: Array<string>) {
    for (let id of storageIds) {
      let requests: RequestPostS3[] = (await this.storageProvider.get(this.getKeyPostPhotoRequest(id), false)) || [];
      if (this.isOnline()) {
        for (let request of requests) {
          await this.postPhotoToS3Online(request, id);
        }
      }
    }
  }

  async retryDeletePhotoFromS3(storageIds: Array<string>) {
    for (let id of storageIds) {
      let requests: RequestDeleteS3[] =
        (await this.storageProvider.get(this.getKeyDeletePhotoRequest(id), false)) || [];
      if (this.isOnline()) {
        for (let request of requests) {
          await this.deletePhotoFromS3Online(request, id);
        }
      }
    }
  }
}
